import { useCallback, useMemo } from 'react';

import { Chart, LegendTilesConfig } from '@dbt-labs/charts';
import { Link, Section } from '@dbt-labs/sourdough';

import { parseDateString } from '../../hooks/charts/useCostInsights';
import { Grouping } from '../../typings/costInsights';
import { formatCredits } from '../../util/costInsights';
import { TEAL_COLOR_WITH_OPACITY } from './const';
import { GroupingSelector } from './GroupingSelector';
import { createEmptyLegendTile, legendTilesBuilder } from './utils';

const PRIMARY_DATA_LABEL = 'Compute executed';
const SECONDARY_DATA_LABEL = 'Total compute without optimization';
const AREA_DATA_LABEL = 'Compute saved';

export type UsageChartDataPoint = {
  date: string; // YYYY-MM-DD format
  creditsUsed: number;
  creditsAvoided: number;
};

type UsageChartProps = {
  data: UsageChartDataPoint[];
  isLoading: boolean;
  isPreparing?: boolean;
  grouping?: Grouping;
  setGrouping?: (grouping: Grouping) => void;
  title?: React.ReactNode;
  selector?: React.ReactNode;
  infoTooltip?: React.ReactNode;
  statusBadge?: React.ReactNode;
  enableGroupingSelector?: boolean;
};

export const UsageChart = ({
  data,
  isLoading,
  isPreparing = false,
  grouping = 'daily',
  setGrouping,
  title,
  selector,
  infoTooltip,
  statusBadge,
  enableGroupingSelector = true,
}: UsageChartProps) => {
  const chartData = useMemo<{
    totalCreditsUsedData: Array<{ date: Date; value: number }>;
    totalCreditsWithoutSavingsData: Array<{ date: Date; value: number }>;
    totalCreditsSavingsData: Array<{ date: Date; value: number }>;
  }>(() => {
    const totalCreditsUsedData: Array<{ date: Date; value: number }> = [];
    const totalCreditsWithoutSavingsData: Array<{ date: Date; value: number }> = [];
    const totalCreditsSavingsData: Array<{ date: Date; value: number }> = [];
    data.forEach((row) => {
      const date = parseDateString(row.date);

      totalCreditsUsedData.push({ date, value: row.creditsUsed });
      totalCreditsWithoutSavingsData.push({
        date,
        value: row.creditsUsed + row.creditsAvoided,
      });
      totalCreditsSavingsData.push({
        date,
        value: row.creditsAvoided,
      });
    });

    return {
      totalCreditsUsedData,
      totalCreditsWithoutSavingsData,
      totalCreditsSavingsData,
    };
  }, [data]);

  const valueAccessor = useCallback((row: any) => row.value, []);
  const formatValue = useCallback((value: number | null) => formatCredits(value), []);

  const isEmpty = !isLoading && data.length === 0;

  const emptyStateLegendTilesBuilder = useCallback(() => {
    return [
      createEmptyLegendTile(PRIMARY_DATA_LABEL, 'var(--fgVizIndigo)'),
      createEmptyLegendTile(SECONDARY_DATA_LABEL, 'var(--fgVizTeal)'),
      createEmptyLegendTile(AREA_DATA_LABEL, TEAL_COLOR_WITH_OPACITY),
    ];
  }, []);

  const legendTilesBuilderCallback = useCallback(
    (config: LegendTilesConfig) =>
      legendTilesBuilder({
        config: config,
        value: chartData.totalCreditsSavingsData.reduce(
          (sum, row) => sum + row.value,
          0,
        ),
        label: AREA_DATA_LABEL,
        color: TEAL_COLOR_WITH_OPACITY,
      }),
    [chartData.totalCreditsSavingsData],
  );

  const defaultTitle = useMemo(
    () => (
      <div className="flex flex-col gap-y-2">
        <div className="flex items-center justify-between gap-x-2">
          <div className="flex items-center gap-x-2">
            <span>Compute usage over time</span>
            {infoTooltip}
            {statusBadge}
          </div>
          {selector !== undefined
            ? selector
            : enableGroupingSelector &&
              setGrouping && (
                <GroupingSelector grouping={grouping} setGrouping={setGrouping} />
              )}
        </div>
        <span className="font-caption">
          Estimated warehouse compute usage by state-aware orchestration during this
          period.{' '}
          <Link
            isInternal={false}
            to="https://docs.getdbt.com/docs/explore/cost-insights"
          >
            Learn more.
          </Link>
        </span>
      </div>
    ),
    [grouping, selector, setGrouping, infoTooltip, statusBadge, enableGroupingSelector],
  );

  const content = (
    <Section header={title !== undefined ? title : defaultTitle}>
      <Chart
        data={chartData.totalCreditsUsedData}
        secondaryData={chartData.totalCreditsWithoutSavingsData}
        primaryColor="var(--fgVizIndigo)"
        secondaryColor="var(--fgVizTeal)"
        fillSecondaryArea={true}
        secondaryAreaStyle={{
          opacity: 0.1,
          color: 'var(--fgVizTeal)',
        }}
        secondaryValueAccessor={(row: any) => row.value}
        isLoading={isLoading || isPreparing}
        loadingMessage={isPreparing ? 'Preparing cost insights...' : undefined}
        seriesOrder={[PRIMARY_DATA_LABEL, SECONDARY_DATA_LABEL]}
        type="timeSeriesLine"
        chartType="line"
        fillArea={true}
        yLabel="Compute usage"
        primaryDataLabel={PRIMARY_DATA_LABEL}
        secondaryDataLabel={SECONDARY_DATA_LABEL}
        timeGrouping={grouping}
        formatValue={formatValue}
        groupAccessor={(row: any) => {
          return row.group;
        }}
        emptyStateMessage="No usage data available to view for this period"
        valueAccessor={valueAccessor}
        legendTilesBuilder={
          isEmpty || isLoading || isPreparing
            ? emptyStateLegendTilesBuilder
            : legendTilesBuilderCallback
        }
      />
    </Section>
  );

  return content;
};
